import React, { useMemo, useState, useRef, useEffect } from "react";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { chatSessionsAPI } from '../utils/api';
import { FiCopy, FiCheck, FiVolume2, FiVolumeX, FiShare2, FiX, FiX as FiClose } from 'react-icons/fi';
import { FaXTwitter, FaFacebook, FaLinkedin, FaRedditAlien } from 'react-icons/fa6';
import DocumentSelector from './DocumentSelector';

export default function ChatWindow({ messages, isLoadingResponse = false, selectedChatId, availableDocuments = [], documentMetadata = [], selectedCodeBook, setSelectedCodeBook, selectedDocuments = [], onDocumentSelectionChange, onCancelStream }) {
  console.log('ChatWindow render - selectedChatId:', selectedChatId, 'messages count:', messages.length);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackQuestion, setFeedbackQuestion] = useState("");
  const [userAnswer, setUserAnswer] = useState("");
  const [confidence, setConfidence] = useState(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [expandedSources, setExpandedSources] = useState(new Set());
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [isReadingIndex, setIsReadingIndex] = useState(null);
  const speechControllerRef = useRef(null);

  const [showShareModal, setShowShareModal] = useState(false);
  const [shareText, setShareText] = useState("");

  const appShareUrl = useMemo(() => {
    try { return window?.location?.origin || ''; } catch { return ''; }
  }, []);

  const openFeedbackModal = (question) => {
    setFeedbackQuestion(question);
    setUserAnswer("");
    setConfidence(null);
    setShowFeedbackModal(true);
  };
  const closeFeedbackModal = () => setShowFeedbackModal(false);

  const toggleSources = (messageIndex) => {
    const newExpanded = new Set(expandedSources);
    if (newExpanded.has(messageIndex)) {
      newExpanded.delete(messageIndex);
    } else {
      newExpanded.add(messageIndex);
    }
    setExpandedSources(newExpanded);
  };

  const handleCopy = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text || '');
      setCopiedIndex(idx);
      setTimeout(() => setCopiedIndex(null), 1500);
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  const handleReadAloud = (text, idx) => {
    try {
      if (!('speechSynthesis' in window)) return;
      const synth = window.speechSynthesis;
      // Toggle off if same index currently reading
      if (isReadingIndex === idx) {
        synth.cancel();
        setIsReadingIndex(null);
        return;
      }
      // stop any ongoing
      synth.cancel();
      const utter = new SpeechSynthesisUtterance((text || '').replace(/[#*_`>\-]/g, ''));
      utter.rate = 1;
      utter.pitch = 1;
      utter.lang = 'en-US';
      speechControllerRef.current = utter;
      utter.onend = () => setIsReadingIndex(null);
      utter.onerror = () => setIsReadingIndex(null);
      setIsReadingIndex(idx);
      synth.speak(utter);
    } catch (e) {
      console.error('TTS failed', e);
      setIsReadingIndex(null);
    }
  };

  const openShare = (text) => {
    setShareText(text || '');
    setShowShareModal(true);
  };
  const closeShare = () => setShowShareModal(false);

  const handleSubmitFeedback = async () => {
    console.log('Submit feedback clicked:', { selectedChatId, feedbackQuestion, userAnswer, confidence });
    if (!selectedChatId || !feedbackQuestion || !userAnswer || !confidence) {
      console.log('Missing required fields:', { selectedChatId, feedbackQuestion, userAnswer, confidence });
      return;
    }
    setFeedbackSubmitting(true);
    setFeedbackSuccess(false);
    try {
      console.log('Calling submitCorrectAnswer API...');
      await chatSessionsAPI.submitCorrectAnswer(selectedChatId, feedbackQuestion, userAnswer, confidence);
      console.log('Feedback submitted successfully');
      setFeedbackSuccess(true);
      setTimeout(() => {
        setShowFeedbackModal(false);
        setFeedbackSuccess(false);
        setFeedbackSubmitting(false);
      }, 1500);
    } catch (e) {
      console.error('Failed to submit feedback:', e);
      setFeedbackSubmitting(false);
      setFeedbackSuccess(false);
      alert('Failed to submit feedback. Please try again.');
    }
  };


  const confidenceGradients = [
    "bg-gradient-to-br from-red-100 via-white to-orange-100",
    "bg-gradient-to-br from-orange-100 via-white to-yellow-100",
    "bg-gradient-to-br from-yellow-100 via-white to-lime-100",
    "bg-gradient-to-br from-lime-100 via-white to-green-100",
    "bg-gradient-to-br from-green-100 via-white to-emerald-100",
  ];
  const confidenceLabels = [
    "Not Confident",
    "Slightly Confident",
    "Neutral",
    "Confident",
    "Very Confident",
  ];
  const confidenceEmojis = [
    "😟",
    "😕",
    "😐",
    "🙂",
    "😃",
  ];

  // (removed unused helpers to keep component clean)

  const messagesContainerRef = useRef(null);

  // Auto-scroll to bottom when messages change or when loading starts/stops
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    // Use rAF to ensure layout is committed before scrolling
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  }, [messages?.length, isLoadingResponse]);

  return (
    <div className="flex-1 flex flex-col bg-gray-50 dark:bg-gray-900 relative min-h-0">
      {/* Header with Document Selector */}
      <div className="flex-shrink-0 relative z-20 p-4 pb-2">
        <div className="flex justify-start">
          <DocumentSelector
            selectedDocuments={selectedDocuments}
            onDocumentSelectionChange={onDocumentSelectionChange}
            className=""
          />
        </div>
      </div>

      {/* Chat Messages Area */}
  <div ref={messagesContainerRef} className="flex-1 overflow-y-auto pretty-scrollbar p-6 pt-2 space-y-4 relative min-h-0">
        {/* Subtle decorative elements without gradients */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-10 right-10 w-32 h-32 bg-indigo-50/40 dark:bg-indigo-900/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-10 w-24 h-24 bg-purple-50/40 dark:bg-purple-900/10 rounded-full blur-3xl"></div>
        </div>

      {/* Selected Documents Indicator */}
      {selectedDocuments && selectedDocuments.length > 0 && (
        <div className="relative z-10 mb-4">
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 backdrop-blur-sm border border-emerald-200/50 dark:border-emerald-700/50 rounded-xl p-4 shadow-sm">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                  Document{selectedDocuments.length > 1 ? 's' : ''} Selected
                </p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {selectedDocuments.map((doc, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-800/50 text-emerald-800 dark:text-emerald-200 border border-emerald-200/50 dark:border-emerald-700/50"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {doc.name}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                  You can ask questions regarding {selectedDocuments.length > 1 ? 'these documents' : 'this document'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="relative z-10">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center simple-fade-in">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                Start a Conversation
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Ask questions about your uploaded documents
              </p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, index) => {
              const filteredClauses = (msg.relevantClauses || []).filter((clause) => {
                const score = typeof clause?.score === 'number' ? clause.score : (typeof clause?.relevance === 'number' ? clause.relevance : 0);
                return score >= 0.15; // show only ≥15% relevance
              });

              return (
              <div
                key={index}
                className={`max-w-4xl px-5 py-4 rounded-2xl shadow-lg transition-all duration-300 text-sm simple-slide-up ${
                  msg.sender === "user"
                    ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white ml-auto shadow-indigo-500/25 max-w-xl"
                    : msg.isError
                    ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 shadow-red-500/10 border border-red-200 dark:border-red-800"
                    : "bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 shadow-gray-500/10 dark:shadow-gray-900/25 border border-gray-100 dark:border-gray-700"
                }`}
                style={{animationDelay: `${index * 0.1}s`}}
              >
                <div className="flex items-start gap-3">
                  {msg.sender !== "user" && (
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      msg.isError 
                        ? 'bg-red-500'
                        : 'bg-gradient-to-br from-indigo-500 to-purple-600'
                    }`}>
                      {msg.isError ? (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className={`prose prose-slate max-w-none prose-headings:scroll-mt-24 prose-pre:bg-gray-900/90 prose-pre:text-gray-100 prose-pre:rounded-xl prose-pre:p-4 prose-table:my-3 prose-table:border prose-th:border prose-td:border prose-th:bg-gray-100/60 dark:prose-th:bg-gray-800/60 ${msg.sender === 'user' ? 'text-white dark:prose-invert' : 'dark:prose-invert'}`}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkBreaks]}
                        rehypePlugins={[rehypeRaw, rehypeSanitize]}
                        components={{
                          a: ({node, ...props}) => <a {...props} target="_blank" rel="noopener noreferrer" />
                        }}
                      >
                        {msg.text || ''}
                      </ReactMarkdown>
                      
                      {/* Streaming Indicator - show a pulsing cursor when message is streaming */}
                      {msg.isStreaming && (
                        <span className="inline-flex items-center ml-1">
                          <span className="inline-block w-1.5 h-4 bg-indigo-500 rounded-sm animate-pulse"></span>
                        </span>
                      )}
                    </div>
                    
                    {/* Sources Section for AI responses with relevant clauses */}
                    {msg.sender === "ai" && Array.isArray(msg.relevantClauses) && (
                      <div className="mt-4">
                        {/* Show loading state if relevantClauses is being fetched */}
                        {msg.isLoadingClauses && (
                          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium border border-indigo-200/50 dark:border-indigo-700/50">
                            <div className="w-3 h-3 border border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                            Loading Sources...
                          </div>
                        )}
                        
                        {/* Sources Button - only show if clauses exist or are being loaded */}
                        {(filteredClauses.length > 0) && (
                          <button
                            onClick={() => toggleSources(index)}
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium hover:from-indigo-100 hover:to-purple-100 dark:hover:from-indigo-800/40 dark:hover:to-purple-800/40 transition-all duration-200 border border-indigo-200/50 dark:border-indigo-700/50 shadow-sm hover:shadow-md"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Sources ({filteredClauses.length})
                            <svg className={`w-3 h-3 transition-transform duration-200 ${expandedSources.has(index) ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        )}

                        {/* Expanded Sources Content */}
                        {expandedSources.has(index) && (
                          <div className="mt-3 p-4 bg-gradient-to-br from-indigo-50/50 to-purple-50/50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-200/50 dark:border-indigo-700/50 backdrop-blur-sm">
                            <h4 className="text-xs font-semibold text-indigo-700 dark:text-indigo-300 mb-3 flex items-center gap-2">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Relevant Clauses from the uploaded document
                            </h4>
                            <div className="space-y-3">
                              {filteredClauses.map((clause, clauseIndex) => (
                                <div key={clauseIndex} className="group">
                                  {/* Clause Pill */}
                                  <div className="flex items-start gap-3 p-3 bg-white/70 dark:bg-gray-800/70 rounded-lg border border-indigo-200/50 dark:border-indigo-700/50 hover:border-indigo-300 dark:hover:border-indigo-600 transition-all duration-200 shadow-sm hover:shadow-md">
                                    {/* Score Badge */}
                                    <div className="flex-shrink-0">
                                      <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                        {Math.round(clause.score * 100)}
                                      </div>
                                    </div>
                                    
                                    {/* Clause Content */}
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                                        <span className="font-medium text-indigo-600 dark:text-indigo-400">Relevance:</span> {Math.round(clause.score * 100)}%
                                      </div>
                                      <div className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                                        {clause.text}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Show search results for AI responses */}
                    {msg.sender === "ai" && msg.searchResults && msg.searchResults.length > 0 && (
                      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                        <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Sources ({msg.totalResults || msg.searchResults.length} results)
                        </h4>
                        <div className="space-y-2">
                          {msg.searchResults.slice(0, 3).map((result, idx) => (
                            <div key={idx} className="p-2 bg-white dark:bg-gray-800 rounded border border-gray-100 dark:border-gray-600">
                              <div className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1">
                                📄 {result.pdfFileName} • {result.section}
                              </div>
                              <div className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                                {result.content}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Button to provide correct answer only when there is a normal AI response (no error) */}
                    {msg.sender === "ai" && !msg.isError && (
                      <div className="mt-4 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <button
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border transition ${copiedIndex===index? 'bg-green-100 border-green-200 text-green-700':'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                            onClick={() => handleCopy(msg.text || '', index)}
                            title="Copy to clipboard"
                          >
                            {copiedIndex===index ? <FiCheck className="w-3.5 h-3.5"/> : <FiCopy className="w-3.5 h-3.5"/>}
                            <span>{copiedIndex===index? 'Copied':'Copy'}</span>
                          </button>
                          <button
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border transition ${isReadingIndex===index? 'bg-indigo-100 border-indigo-200 text-indigo-700':'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                            onClick={() => handleReadAloud(msg.text || '', index)}
                            title="Read aloud"
                          >
                            {isReadingIndex===index ? <FiVolumeX className="w-3.5 h-3.5"/> : <FiVolume2 className="w-3.5 h-3.5"/>}
                            <span>{isReadingIndex===index? 'Stop':'Read aloud'}</span>
                          </button>
                          <button
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                            onClick={() => openShare(msg.text || '')}
                            title="Share"
                          >
                            <FiShare2 className="w-3.5 h-3.5"/>
                            <span>Share</span>
                          </button>
                        </div>
                        <button
                          className="px-3 py-1.5 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-200 dark:hover:bg-yellow-800 transition-all text-xs font-semibold shadow"
                          onClick={() => openFeedbackModal(messages[index - 1]?.text || "")}
                        >
                          Submit correct answer
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
                );
              })}

            {/* Loading indicator - now hidden since we use streaming messages with inline cursor */}
            {/* The streaming message itself shows the cursor indicator, so no separate loading box needed */}
          </>
        )}
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 dark:bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-10 max-w-xl w-full border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Original Question</h3>
            <div className="mb-4 p-3 rounded bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700">
              {feedbackQuestion}
            </div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Your Correct Answer</label>
            <textarea
              className="w-full p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 mb-4"
              rows={4}
              value={userAnswer}
              onChange={e => setUserAnswer(e.target.value)}
              placeholder="Type your correct answer here..."
            />
            <div className="mb-4">
              <div className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200 text-center">How much are you confident with your answer?</div>
              <div className="flex justify-center gap-6">
                {[1,2,3,4,5].map((num, idx) => (
                  <div key={num} className="flex flex-col items-center">
                    <button
                      type="button"
                      className={`w-12 h-12 rounded-full font-bold text-lg focus:outline-none border-2 border-white/60 dark:border-gray-700 shadow-lg backdrop-blur-md transition-all duration-200 ${confidenceGradients[idx]} ${confidence === num ? 'scale-110 ring-4 ring-offset-2 ring-indigo-400 dark:ring-indigo-700 border-indigo-400 dark:border-indigo-500' : 'hover:scale-105 hover:shadow-xl'} flex items-center justify-center`}
                      onClick={() => setConfidence(num)}
                      style={{boxShadow: confidence === num ? '0 4px 24px 0 rgba(99,102,241,0.15)' : undefined}}
                      aria-label={confidenceLabels[idx]}
                    >
                      <span className="text-2xl">{confidenceEmojis[idx]}</span>
                    </button>
                    <span className={`mt-2 text-xs text-center w-20 ${confidence === num ? 'text-indigo-600 dark:text-indigo-300 font-semibold' : 'text-gray-400 dark:text-gray-500'}`}>{confidenceLabels[idx]}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              {!selectedChatId && (
                <div className="flex-1 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg border border-red-200 dark:border-red-800">
                  ⚠️ No active chat session. Please start a conversation first.
                </div>
              )}
              <button
                className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
                onClick={closeFeedbackModal}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 rounded-lg transition-all ${
                  !userAnswer || !confidence || feedbackSubmitting || !selectedChatId
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-indigo-500 text-white hover:bg-indigo-600'
                }`}
                onClick={handleSubmitFeedback}
                disabled={!userAnswer || !confidence || feedbackSubmitting || !selectedChatId}
                title={!selectedChatId ? 'No active chat session' : !userAnswer ? 'Please enter your answer' : !confidence ? 'Please select confidence level' : 'Submit feedback'}
              >
                {feedbackSubmitting ? 'Submitting...' : feedbackSuccess ? 'Submitted!' : 'Submit Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Dim backdrop */}
          <div className="absolute inset-0 bg-black/40 dark:bg-black/60" onClick={closeShare} />
          {/* Card */}
          <div className="relative mx-4 w-full max-w-2xl">
            <div className="relative overflow-hidden rounded-2xl border border-gray-200/70 dark:border-gray-700/60 bg-white dark:bg-gray-900 shadow-xl">
              {/* Header */}
              <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Share Answer</h3>
                <button
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={closeShare}
                  aria-label="Close share"
                >
                  <FiClose className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              {/* Preview */}
              <div className="px-5 pt-4">
                <div className="relative rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-4">
                  <div className="prose prose-slate max-w-none dark:prose-invert text-sm max-h-48 overflow-hidden">
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeRaw, rehypeSanitize]}>
                      {shareText}
                    </ReactMarkdown>
                  </div>
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-gray-50 dark:from-gray-800 to-transparent rounded-b-xl"></div>
                </div>
              </div>
              {/* Actions */}
              <div className="px-5 py-4 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => navigator.clipboard.writeText(shareText || '')}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
                >
                  <FiCopy className="w-4 h-4"/>
                  Copy
                </button>
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs bg-black text-white hover:bg-gray-900"
                >
                  <FaXTwitter className="w-4 h-4"/>
                  X
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(appShareUrl)}&quote=${encodeURIComponent(shareText)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs bg-[#1877F2]/10 text-[#1877F2] hover:bg-[#1877F2]/20 border border-[#1877F2]/20"
                >
                  <FaFacebook className="w-4 h-4"/>
                  Facebook
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(appShareUrl)}&summary=${encodeURIComponent(shareText)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs bg-[#0A66C2]/10 text-[#0A66C2] hover:bg-[#0A66C2]/20 border border-[#0A66C2]/20"
                >
                  <FaLinkedin className="w-4 h-4"/>
                  LinkedIn
                </a>
                <a
                  href={`https://www.reddit.com/submit?url=${encodeURIComponent(appShareUrl)}&title=${encodeURIComponent('Interesting answer from RAG AI')}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs bg-[#FF4500]/10 text-[#FF4500] hover:bg-[#FF4500]/20 border border-[#FF4500]/20"
                >
                  <FaRedditAlien className="w-4 h-4"/>
                  Reddit
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
